// src/App.jsx
import React, { useContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Login from "./pages/auth/Login";
import Dashboard from "./pages/dashboard/Dashboard";
import AddProject from "./pages/projects/AddProject";
import ProjectsList from "./pages/projects/ProjectsList";
import ProjectDetail from "./pages/projects/ProjectDetail";
import EditProject from "./pages/projects/EditProject";
import MilestoneManager from "./pages/projects/MilestoneManager";
import ExtraChargeManager from "./pages/projects/ExtraChargeManager";
import DiscussionManager from "./pages/discussion/DiscussionManager";
import BugList from "./pages/bugs/BugList";
import CreateBug from "./pages/bugs/CreateBug";
import BugDetail from "./pages/bugs/BugDetail";
import EditBug from "./pages/bugs/EditBug";
import TaskList from "./pages/tasks/TaskList";
import CreateTask from "./pages/tasks/CreateTask";
import TaskDetail from "./pages/tasks/TaskDetail";
import EditTask from "./pages/tasks/EditTask";
import PaymentList from "./pages/payments/PaymentList";
import EditPayment from "./pages/payments/EditPayment";
import AddPayment from "./pages/payments/AddPayment";
import OverduePayments from "./pages/payments/OverduePayments";
import ExtraWorkList from "./pages/extra-work/ExtraWorkList";
import SkillDetail from "./pages/skills/SkillDetail";
import SkillList from "./pages/skills/SkillList";
import SkillForm from "./pages/skills/SkillForm";
import { AuthProvider, AuthContext } from "./context/AuthContext";
import PrivateRoute from "./components/PrivateRoute";
import InstallAppPopup from "./components/InstallAppPopup";
import MainLayout from "./layouts/MainLayout";

const RootRedirect = () => {
  const { user } = useContext(AuthContext);

  return <Navigate to={user ? "/dashboard" : "/login"} replace />;
};

const PublicRoute = ({ children }) => {
  const { user } = useContext(AuthContext);

  return user ? <Navigate to="/dashboard" replace /> : children;
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <InstallAppPopup />

        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <MainLayout>
                  <Dashboard />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <PrivateRoute>
                <MainLayout>
                  <ProjectsList />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <PrivateRoute>
                <MainLayout>
                  <ProjectDetail />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/:id/milestones"
            element={
              <PrivateRoute>
                <MainLayout>
                  <MilestoneManager />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/:id/extra-charges"
            element={
              <PrivateRoute>
                <MainLayout>
                  <ExtraChargeManager />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/:id/discussions"
            element={
              <PrivateRoute>
                <MainLayout>
                  <DiscussionManager />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/:id/bugs"
            element={
              <PrivateRoute>
                <MainLayout>
                  <BugList />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/:id/tasks"
            element={
              <PrivateRoute>
                <MainLayout>
                  <TaskList />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/edit/:id"
            element={
              <PrivateRoute>
                <MainLayout>
                  <EditProject />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <PrivateRoute>
                <MainLayout>
                  <TaskList />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/tasks/add"
            element={
              <PrivateRoute>
                <MainLayout>
                  <CreateTask />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/tasks/:id"
            element={
              <PrivateRoute>
                <MainLayout>
                  <TaskDetail />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/tasks/edit/:id"
            element={
              <PrivateRoute>
                <MainLayout>
                  <EditTask />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/bugs"
            element={
              <PrivateRoute>
                <MainLayout>
                  <BugList />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/bugs/add"
            element={
              <PrivateRoute>
                <MainLayout>
                  <CreateBug />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/bugs/:id/edit"
            element={
              <PrivateRoute>
                <MainLayout>
                  <EditBug />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/bugs/:id"
            element={
              <PrivateRoute>
                <MainLayout>
                  <BugDetail />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/extra-work"
            element={
              <PrivateRoute>
                <MainLayout>
                  <ExtraWorkList />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <PrivateRoute>
                <MainLayout>
                  <PaymentList />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/payments/:projectId"
            element={
              <PrivateRoute>
                <MainLayout>
                  <PaymentList />
                </MainLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/payments/add/:projectId"
            element={
              <PrivateRoute>
                <MainLayout>
                  <AddPayment />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/payments/overdue"
            element={
              <PrivateRoute>
                <MainLayout>
                  <OverduePayments />
                </MainLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/payments/edit/:paymentId/:projectId"
            element={
              <PrivateRoute>
                <MainLayout>
                  <EditPayment />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/add"
            element={
              <PrivateRoute>
                <MainLayout>
                  <AddProject />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/skills"
            element={
              <PrivateRoute>
                <MainLayout>
                  <SkillList />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/skills/add"
            element={
              <PrivateRoute>
                <MainLayout>
                  <SkillForm />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/skills/edit/:id"
            element={
              <PrivateRoute>
                <MainLayout>
                  <SkillForm />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/skills/:id"
            element={
              <PrivateRoute>
                <MainLayout>
                  <SkillDetail />
                </MainLayout>
              </PrivateRoute>
            }
          />
          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
};

export default App;
